import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import { makeStyles } from 'tss-react/mui';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';

import CommonButton from '../../../components/buttons/CommonButton';
import FormLinkButton from '../../../components/buttons/FormLinkButton';
import { StatusChip } from '../../../components/StatusChip';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';
import StatusBadge from '../../PayoutsView/StatusBadge';
import { getMaskedAccountDisplay, getSettlementPath } from './helpers';
import { SettlementPayoutSummary } from './types';

const useStyles = makeStyles()((theme: Theme) => ({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '8px 16px',
  },
  sectionTitle: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '20px',
  },
  sectionDescription: {
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '13px',
    marginTop: '4px',
    maxWidth: '600px',
  },
  sectionSummary: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 600,
  },
  readOnlyBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: '999px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '11px',
    fontWeight: 600,
  },
  container: {
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '10px',
    backgroundColor: theme.palette.primary.light,
    overflowX: 'auto',

    '&::-webkit-scrollbar': {
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  table: {
    // Below this width the table scrolls horizontally instead of squashing its columns.
    minWidth: '960px',
    '& .MuiTableCell-root': {
      whiteSpace: 'nowrap',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${theme.palette.secondary.dark}`,
      fontFamily: 'Inter',
      fontSize: '12px',
      fontWeight: 500,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      padding: '14px 16px',
      whiteSpace: 'nowrap',
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
      borderBottom: `1px solid ${theme.palette.secondary.dark}`,
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      fontSize: '14px',
      padding: '12px 16px',
      verticalAlign: 'middle',
    },
    '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  mutedTable: {
    '& .MuiTableBody-root .MuiTableCell-root': {
      color: 'rgba(255, 255, 255, 0.6)',
    },
  },
  primaryText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
  },
  amount: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  paidToCell: {
    // Addresses can be long; wrap within a fixed width instead of stretching the table.
    whiteSpace: 'normal !important' as 'normal',
    width: '280px',
    maxWidth: '280px',
    minWidth: '200px',
    wordBreak: 'break-word',
  },
  clampedText: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  expandButton: {
    minWidth: 0,
    padding: 0,
    marginTop: '2px',
    color: theme.palette.info.main,
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px',
    '& button': {
      whiteSpace: 'nowrap',
    },
  },
  emptyText: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontFamily: 'Inter',
    fontSize: '13px',
    fontStyle: 'italic',
    padding: '16px',
  },
}));

const getMethodLabel = (paymentMethod: string | null) =>
  paymentMethod === 'ACH'
    ? 'ACH'
    : paymentMethod === 'CHECK'
    ? 'Check'
    : paymentMethod === 'V'
    ? 'V'
    : '—';

/** Where the money went — the settlement's own ACH account or mailing address. */
const getPaidToLabel = ({
  paymentMethod,
  achDetails,
  mailingAddress,
  isPaidExternally,
  externalPaymentDetails,
}: SettlementPayoutSummary) => {
  if (isPaidExternally) {
    return [
      'Paid externally',
      externalPaymentDetails?.externalPaymentMethod,
      externalPaymentDetails?.externalPaymentReference,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (paymentMethod === 'ACH') {
    const account = getMaskedAccountDisplay(achDetails?.accountNumber, achDetails?.accountNumberLast4);
    return ['ACH', account].filter(Boolean).join(' · ');
  }

  if (paymentMethod === 'CHECK') {
    const stateZip = [mailingAddress?.state, mailingAddress?.zip].filter(Boolean).join(' ');
    const address = [mailingAddress?.line1, mailingAddress?.line2, mailingAddress?.city, stateZip]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(', ');
    return ['Check', address].filter(Boolean).join(' · ');
  }

  return getMethodLabel(paymentMethod);
};

/** Wraps long "Paid To" values to two lines, with a toggle to reveal the rest. */
const PaidToCell = ({ settlement }: { settlement: SettlementPayoutSummary }) => {
  const { classes } = useStyles();
  const textRef = useRef<HTMLSpanElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const label = getPaidToLabel(settlement);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [label, expanded]);

  return (
    <TableCell className={classes.paidToCell}>
      <span
        ref={textRef}
        className={expanded ? undefined : classes.clampedText}
        title={expanded ? undefined : label}
      >
        {label}
      </span>
      {(isOverflowing || expanded) && (
        <Button
          className={classes.expandButton}
          disableRipple
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </TableCell>
  );
};

interface SettlementCellsProps {
  settlement: SettlementPayoutSummary;
  className: string;
}

const CaseCell = ({ settlement, className }: SettlementCellsProps) => (
  <TableCell>
    <Typography className={className}>{settlement.caseReferenceId ?? '—'}</Typography>
  </TableCell>
);

const SettlementCell = ({ settlement, className }: SettlementCellsProps) => (
  <TableCell>
    <Box display="flex" alignItems="center" gap="6px" sx={{ '& > a': { marginBottom: 0 } }}>
      <Typography className={className}>Settlement #{settlement.settlementId}</Typography>
      <FormLinkButton path={getSettlementPath(settlement.settlementId)} />
    </Box>
  </TableCell>
);


interface SettlementPayoutTablesProps {
  clientId: string;
  settlements: SettlementPayoutSummary[];
  canViewPlaintiffOnboarding: boolean;
  reasonCodes: string[];
  onSaved: () => void;
  onReInitiate: (settlementId: number) => Promise<void>;
  reInitiatingId: number | null;
}

const SettlementPayoutTables = ({
  settlements,
  canViewPlaintiffOnboarding,
  onReInitiate,
  reInitiatingId,
}: SettlementPayoutTablesProps) => {
  const styles = useStyles();
  const { classes, cx } = styles;
  const ongoing = settlements.filter((s) => !s.isPayoutCompleted);
  const completed = settlements.filter((s) => s.isPayoutCompleted);
  const completedTotal = completed.reduce((sum, s) => sum + (s.netAmount ?? 0), 0);

  return (
    <Box display="flex" flexDirection="column" gap="32px">
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Box>
            <Typography className={classes.sectionTitle}>Ongoing Payouts</Typography>
            <Typography className={classes.sectionDescription}>
              Each settlement pays to exactly one payout method.
            </Typography>
          </Box>
        </Box>

        <TableContainer className={classes.container}>
          {ongoing.length === 0 ? (
            <Typography className={classes.emptyText}>No ongoing payouts.</Typography>
          ) : (
            <Table className={classes.table}>
              <colgroup>
                <col style={{ width: '120px' }} />
                <col style={{ width: '170px' }} />
                <col style={{ width: '130px' }} />
                <col />
                <col style={{ width: '220px' }} />
                <col style={{ width: '140px' }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>Case</TableCell>
                  <TableCell>Settlement</TableCell>
                  <TableCell align="right">Net Amount</TableCell>
                  <TableCell>Paid To</TableCell>
                  <TableCell>Payout Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {ongoing.map((settlement) => {
                  const { settlementId } = settlement;
                  const canReInitiate =
                    canViewPlaintiffOnboarding && settlement.isReinitiateEnabled;
                  const reInitiating = reInitiatingId === settlementId;

                  return (
                    <Fragment key={settlementId}>
                      <TableRow>
                        <CaseCell settlement={settlement} className={classes.primaryText} />
                        <SettlementCell settlement={settlement} className={classes.primaryText} />
                        <TableCell align="right" className={classes.amount}>
                          {numberToUSDCurrency(settlement.netAmount)}
                        </TableCell>
                        <PaidToCell settlement={settlement} />
                        <TableCell>
                          {settlement.payoutStatus ? (
                            <StatusBadge status={settlement.payoutStatus} />
                          ) : (
                            <StatusChip
                              label="Not Started"
                              color="rgba(255, 255, 255, 0.5)"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box className={classes.actions}>
                            {canReInitiate && (
                              <CommonButton
                                buttonText={reInitiating ? 'Re-initiating...' : 'Re-initiate'}
                                disabled={reInitiating}
                                minWidth="130px"
                                onButtonClick={() => void onReInitiate(settlementId)}
                                type="teal"
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Box>

      {completed.length > 0 && (
        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>
            <Box>
              <Box display="flex" alignItems="center" gap="10px">
                <LockOutlinedIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '18px' }} />
                <Typography className={classes.sectionTitle}>Completed Payouts</Typography>
                <Box className={classes.readOnlyBadge}>Read only</Box>
              </Box>
              <Typography className={classes.sectionDescription}>
                Paid settlements are locked. Each row keeps the method the money actually went to.
              </Typography>
            </Box>
            <Typography className={classes.sectionSummary}>
              {completed.length} {completed.length === 1 ? 'payout' : 'payouts'} ·{' '}
              {numberToUSDCurrency(completedTotal)} paid
            </Typography>
          </Box>

          <TableContainer className={classes.container}>
            <Table className={cx(classes.table, classes.mutedTable)}>
              <colgroup>
                <col style={{ width: '120px' }} />
                <col style={{ width: '170px' }} />
                <col style={{ width: '130px' }} />
                <col />
                <col style={{ width: '220px' }} />
                <col style={{ width: '140px' }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell>Case</TableCell>
                  <TableCell>Settlement</TableCell>
                  <TableCell align="right">Net Amount</TableCell>
                  <TableCell>Paid To</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completed.map((settlement) => (
                  <TableRow key={settlement.settlementId}>
                    <CaseCell settlement={settlement} className={classes.primaryText} />
                    <SettlementCell settlement={settlement} className={classes.primaryText} />
                    <TableCell align="right" className={classes.amount}>
                      {numberToUSDCurrency(settlement.netAmount)}
                    </TableCell>
                    <PaidToCell settlement={settlement} />
                    <TableCell>
                      {settlement.completedAt
                        ? moment(settlement.completedAt).format('MMM D, YYYY h:mm:ss A')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={settlement.payoutStatus ?? 'Paid'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default SettlementPayoutTables;
